import { Box, Checkbox, FormControlLabel, Grid, MenuItem, Select, TextField } from "@mui/material";
import * as React from "react";
import { AvatarImage } from "ui/Scripts/source/components/framework/Avatar/AvatarImage";
import { Base } from "ui/Scripts/source/framework/base";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { ConfirmationDialogResult, EnumHelper, InstantMessageUserInvitationAcceptType, UserType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";
import { AppUtils } from "../../../framework/appUtils";
import * as store from "../../../framework/customStore";
import { SaveDataObj } from "../../../framework/saveData";
import * as userService from "../../../models/services/userService";
import { ISessionState } from "../../../models/session/sessionState";
import * as storeActions from "../../../models/store/storeActions";
import { IUserProfileEditItem } from "../../../models/user/userProfileEditItem";
import { Dialog } from "../../framework/Dialog/Dialog";
import { FormField } from "../../../../../../QTeamSystems.QTeamIntra.Ui/Scripts/source/components/framework/FormField";
import { GridRow } from "../../framework/Grid/GridRow";
import { UserChangePasswordDialog } from "./UserChangePasswordDialog";
import { ChangePasswordButton } from "source/components/framework/Button/ChangePasswordButton";
import { DialogItemsNav } from "source/components/framework/Dialog/DialogItemsNav";
import { DialogButtons } from "source/components/framework/Dialog/DialogButtons";
import { ICompanyMenuItem } from "ui/Scripts/source/models/company/companyMenuItem";
import { Dropdown } from "ui/Scripts/source/components/framework/Dropdown/Dropdown";
import { msalPopupLoginForAccountLinking } from "ui/Scripts/source/models/common/ssoUtils";
import { IUserMicrosoftAccount, UserMicrosoftAccount } from "ui/Scripts/source/models/user/userMicrosoftAccount";
import { AppButton } from "ui/Scripts/source/components/framework/Button/AppButton";
import AddLinkIcon from "@mui/icons-material/AddLink";
import LinkOffIcon from "@mui/icons-material/LinkOff";

export interface IUserProfileDialogProp {
    sessionState: ISessionState;
    classes?: string;
    onRefreshSessionState: () => void;
    onOk: () => void;
    onCancel: () => void;
}

export interface IUserProfileDialogState {
    isLoading: boolean;
    qrCodeLoginUrlTemplate: string;
    allowedCompanies: ICompanyMenuItem[];
    file: IFileAttachment;
    picture: string;
    resetPicture: boolean;
    userProfile: IUserProfileEditItem;
    rowId: string;
    firstName: string;
    lastName: string;
    email: string;
    userType: UserType;
    qrCodeId: string;
    loginCompanyCode: string;
    emailNotificationType: number;
    pushNotificationType: number;
    instantMessageUserInvitationAcceptType: number;
    showChangePasswordDialog: boolean;
    minPasswordLength: number;
    msAuthenticationEnabled: boolean;
    msAuthenticationClientId: string;
    msAuthenticationRedirectUri: string;
    microsoftAccount: IUserMicrosoftAccount;
}

export class UserProfileDialog extends React.Component<IUserProfileDialogProp, IUserProfileDialogState> {
    private orgStateHash: string = "";

    constructor(props: IUserProfileDialogProp) {
        super(props);
        this.state = {
            isLoading: false,
            qrCodeLoginUrlTemplate: "",
            allowedCompanies: [],
            userProfile: null,
            file: null,
            picture: "",
            resetPicture: false,
            rowId: "",
            firstName: "",
            lastName: "",
            email: "",
            userType: UserType.Normal,
            qrCodeId: "",
            loginCompanyCode: "",
            emailNotificationType: 0,
            pushNotificationType: 0,
            instantMessageUserInvitationAcceptType: 0,
            showChangePasswordDialog: false,
            minPasswordLength: 8,
            msAuthenticationEnabled: false,
            msAuthenticationClientId: "",
            msAuthenticationRedirectUri: "",
            microsoftAccount: null,
        };
    }

    loadUserProfile = async () => {
        this.setState({ isLoading: true });
        const result = await AppUtils.callService(() => userService.getUserProfileEdit(), false, false, () => this.setState({ isLoading: false }));
        if (!result) return;
        const userProfile = result.userProfile;
        const state = {
            ...this.state,
            qrCodeLoginUrlTemplate: result.qrCodeLoginUrlTemplate,
            allowedCompanies: result.allowedCompanies,
            userProfile,
            picture: userProfile.picture,
            resetPicture: !userProfile.picture,
            rowId: userProfile.rowId,
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            email: userProfile.email,
            userType: userProfile.userType,
            qrCodeId: userProfile.qrCodeId,
            loginCompanyCode: userProfile.loginCompanyCode,
            emailNotificationType: userProfile.emailNotificationType,
            pushNotificationType: userProfile.pushNotificationType,
            instantMessageUserInvitationAcceptType: userProfile.instantMessageUserInvitationAcceptType,
            minPasswordLength: result.minPasswordLength,
            msAuthenticationEnabled: result.msAuthenticationEnabled,
            msAuthenticationClientId: result.msAuthenticationClientId,
            msAuthenticationRedirectUri: result.msAuthenticationRedirectUri,
            isLoading: false,
            microsoftAccount: userProfile.microsoftAccount,
        };
        this.setState(state);
        const saveData = await this.getSaveDataFromState(this.props, state);
        this.orgStateHash = saveData.hash;
    };

    componentDidMount(): void {
        this.loadUserProfile();
    }

    handleChange = (event) => {
        const target = event.target;
        const value: string = target.value;
        const name: string = target.name;
        if (name === "firstName") {
            this.setState({ firstName: value });
        } else if (name === "lastName") {
            this.setState({ lastName: value });
        } else if (name === "email") {
            this.setState({ email: value });
        } else if (name === "instantMessageUserInvitationAcceptType") {
            this.setState({ instantMessageUserInvitationAcceptType: value.toInteger() });
        }
    };

    handleSetPicture = async (file: File) => {
        if (!file) return;
        const attachment = await FileAttachment.fileToFileAttachment(file, 0);
        if (!attachment) return;
        if (!AppUtils.validateFileAttachment(attachment)) return;
        this.setState({ file: attachment, resetPicture: false, picture: attachment.thumbnail });
    };

    handleRemovePicture = () => {
        this.setState({ file: null, resetPicture: true, picture: "" });
    };

    handleOnChangePassword = () => {
        this.setState({
            showChangePasswordDialog: true
        });
    };

    handleUserChangePasswordDialogOk = (rowId: string) => {
        this.setState({
            showChangePasswordDialog: false,
            rowId
        });
        this.orgStateHash = SaveDataObj.updateHashValue(this.orgStateHash, "rowId", rowId);
    };

    handleUserChangePasswordDialogCancel = () => {
        this.setState({
            showChangePasswordDialog: false
        });
    };

    getSaveDataFromState = async (props: IUserProfileDialogProp, state: IUserProfileDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("rowId", state.rowId);
        result.append("firstName", state.firstName);
        result.append("lastName", state.lastName);
        result.append("email", state.email);
        result.append("loginCompanyCode", state.loginCompanyCode);
        result.append("emailNotificationType", state.emailNotificationType);
        result.append("pushNotificationType", state.pushNotificationType);
        result.append("instantMessageUserInvitationAcceptType", state.instantMessageUserInvitationAcceptType);
        result.append("resetImage", state.resetPicture);
        if (state.file) {
            result.append("attachments", await SaveAttachmentBase64.fromFileAttachments([state.file]));
        }
        return result;
    };
    
    checkErrors = (): Promise<boolean> => {
        const state = this.state;
        const isQrCodeAccount = EnumHelper.isEqual(state.userType, UserType.QrCode);
        return new Promise<boolean>((resolve) => {
            if (!isQrCodeAccount && !state.firstName) {
                AppUtils.showErrorMessage(Translations.FirstNameMustBeDefined);
                return resolve(false);
            }
            if (!state.lastName) {
                AppUtils.showErrorMessage(!isQrCodeAccount ? Translations.LastNameMustBeDefined : Translations.NameMustBeDefined);
                return resolve(false);
            }
            if (!isQrCodeAccount && !Base.isValidEmailAddress(state.email)) {
                AppUtils.showErrorMessage(Translations.EmailAddressMustBeValid);
                return resolve(false);
            }
            return resolve(true);
        });
    };

    saveToDb = async () => {
        const props = this.props;
        const state = this.state;
        if (!await AppUtils.validate(this.checkErrors)) return;
        const saveData = await this.getSaveDataFromState(props, state);
        //Call server
        const result = await AppUtils.callService(() => userService.saveUserProfile(saveData.obj));
        if (!result) return;
        props.onRefreshSessionState();
        props.onOk();
    };

    handleSave = () => {
        this.saveToDb();
    };

    handleCancel = async () => {
        const props = this.props;
        const state = this.state;
        const cancelResult = await AppUtils.cancel((await this.getSaveDataFromState(props, state)).hash, this.orgStateHash, null);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            this.saveToDb();
        } else {
            props.onCancel();
        }
    };

    renderCompany = (company: ICompanyMenuItem) => {
        return <Box sx={{ display: "flex", alignItems: "center" }}>
            <AvatarImage
                size={20}
                name={company.name}
                image={company.logo}
            />
            <Box sx={{ paddingLeft: "10px" }}>{company.name}</Box>
        </Box>;
    };

    getMicrosoftAccountSaveDataObj = (userMicrosoftAccount: UserMicrosoftAccount) => {
        const result = new SaveDataObj();
        result.append("microsoftAccount", userMicrosoftAccount);
        return result.obj;
    };

    handleConnectToMicrosoftAccount = async () => {
        const state = this.state;
        const msalLoginResult = await msalPopupLoginForAccountLinking(state.msAuthenticationClientId, state.msAuthenticationRedirectUri, "login");
        if (!msalLoginResult) return;
        const microsoftAccount = new UserMicrosoftAccount();
        microsoftAccount.homeAccountId = msalLoginResult.account.homeAccountId;
        microsoftAccount.username = msalLoginResult.account.username;
        microsoftAccount.name = msalLoginResult.account.name;
        microsoftAccount.tenantId = msalLoginResult.tenantId;
        const saveResult = await userService.saveThirdPartyAuthentication(this.getMicrosoftAccountSaveDataObj(microsoftAccount));
        if (!saveResult) return;
        this.setState({ microsoftAccount });
    };
    
    handleDisconnectFromMicrosoftAccount = async () => {
        const saveResult = await userService.saveThirdPartyAuthentication(this.getMicrosoftAccountSaveDataObj(null));
        if (!saveResult) return;
        this.setState({ microsoftAccount: null });
    };

    render() {
        const props = this.props;
        const state = this.state;
        if (!state.userProfile) return null;
        const allowedCompany = state.allowedCompanies.find((company) => company.code === state.loginCompanyCode) ?? state.allowedCompanies[0];
        const isQrCodeAccount = EnumHelper.isEqual(state.userType, UserType.QrCode);
        const showMsAuthentication = !isQrCodeAccount && !!state.msAuthenticationEnabled;
        return (
            <Dialog
                id="user-profile-dialog"
                maxWidth="sm"
                title={Translations.UserProfile}
                body={
                    <>
                        <GridRow>
                            <Grid item sm={showMsAuthentication ? 6 : 12}>
                                <Box display="flex" alignItems="center">
                                    <AvatarImage
                                        size={60}
                                        marginRight="1rem"
                                        id={state.userProfile.code}
                                        name={state.lastName + " " + state.firstName}
                                        image={state.picture}
                                        editable={true}
                                        tooltip={Translations.CkickToChangeThePicture}
                                        removeTooltip={Translations.RemovePicture}
                                        onChange={this.handleSetPicture}
                                        onClear={state.resetPicture ? null : this.handleRemovePicture}
                                        onError={(message) => { store.customStore.dispatch(storeActions.showErrorMessage(message)); }}
                                    />
                                    <FormField title={Translations.UserName} margin={0}>
                                        {state.userProfile.code}
                                    </FormField>
                                </Box>
                            </Grid>
                            {showMsAuthentication &&
                                <Grid item sm={6}>
                                    <FormField title={Translations.MicrosoftAccount} margin={0}>
                                        {(!state.microsoftAccount
                                            ? <>
                                                <AppButton
                                                    variant="contained"
                                                    startIcon={<AddLinkIcon />}
                                                    onClick={this.handleConnectToMicrosoftAccount}
                                                >
                                                    {Translations.ConnectToMicrosoftAccount}
                                                </AppButton>
                                            </>
                                            : <>
                                                <Box marginBottom=".5rem">
                                                    {state.microsoftAccount.username}
                                                </Box>
                                                <AppButton
                                                    variant="contained"
                                                    startIcon={<LinkOffIcon />}
                                                    onClick={this.handleDisconnectFromMicrosoftAccount}
                                                >
                                                    {Translations.DisconnectFromMicrosoftAccount}
                                                </AppButton>
                                            </>
                                        )}
                                    </FormField>
                                </Grid>
                            }
                        </GridRow>
                        {(!isQrCodeAccount
                            ? <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.FirstName} required>
                                            <TextField type="text" name="firstName" inputProps={{ maxLength: 50 }} value={state.firstName} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.LastName} required>
                                            <TextField type="text" name="lastName" inputProps={{ maxLength: 50 }} value={state.lastName} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.EmailAddress}>
                                            <TextField type="text" name="email" inputProps={{ maxLength: 50 }} value={state.email} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                            : <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Name} required margin=".4rem 0 0 0">
                                            <TextField type="text" name="lastName" inputProps={{ maxLength: 50 }} value={state.lastName} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.AdditionalIdentifier}>
                                            <TextField type="text" name="firstName" inputProps={{ maxLength: 50 }} value={state.firstName} onChange={this.handleChange} size="small" fullWidth />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        )}
                        {state.allowedCompanies.length > 1 &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.LoginCompany}>
                                        <Dropdown
                                            selectedTitle={this.renderCompany(allowedCompany)}
                                            actions={state.allowedCompanies.map((company) => {
                                                return {
                                                    title: this.renderCompany(company),
                                                    onClick: () => this.setState({ loginCompanyCode: company.code })
                                                };
                                            })}
                                        />
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {!isQrCodeAccount &&
                            <>
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.NotificationSettings}>
                                            <FormControlLabel
                                                label={Translations.EmailNotificationInUse}
                                                control={
                                                    <Checkbox
                                                        checked={state.emailNotificationType > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            this.setState({ emailNotificationType: checked ? 1 : 0 });
                                                        }}
                                                    />
                                                }
                                                slotProps={{ typography: { variant: "body2" } }}
                                            />
                                            <FormControlLabel
                                                label={Translations.PushNotificationInUse}
                                                control={
                                                    <Checkbox
                                                        checked={state.pushNotificationType > 0}
                                                        onChange={(_e, checked: boolean) => {
                                                            this.setState({ pushNotificationType: checked ? 1 : 0 });
                                                        }}
                                                    />
                                                }
                                                slotProps={{ typography: { variant: "body2" } }}
                                            />
                                        </FormField>
                                    </Grid>
                                </GridRow>
                                {props.sessionState.canUseInstantMessages &&
                                    <GridRow>
                                        <Grid item sm={12}>
                                            <FormField title={Translations.InstantMessageSettings}>
                                                <Select name="instantMessageUserInvitationAcceptType" value={state.instantMessageUserInvitationAcceptType.toString(10)} onChange={this.handleChange} size="small" fullWidth>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.CannotInvite} value={InstantMessageUserInvitationAcceptType.CannotInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCannotInvite}</MenuItem>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.CanInvite} value={InstantMessageUserInvitationAcceptType.CanInvite.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeCanInvite}</MenuItem>
                                                    <MenuItem key={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation} value={InstantMessageUserInvitationAcceptType.AutoAcceptInvitation.toString(10)}>{Translations.InstantMessageUserInvitationAcceptTypeAutoAcceptInvitation}</MenuItem>
                                                </Select>
                                            </FormField>
                                        </Grid>
                                    </GridRow>
                                }
                                <GridRow>
                                    <Grid item sm={12}>
                                        <FormField title={Translations.Password}>
                                            <ChangePasswordButton
                                                onClick={this.handleOnChangePassword}
                                            >
                                                {Translations.ChangePassword}
                                            </ChangePasswordButton>
                                        </FormField>
                                    </Grid>
                                </GridRow>
                            </>
                        }
                        {isQrCodeAccount &&
                            <GridRow>
                                <Grid item sm={12}>
                                    <FormField title={Translations.Link}>
                                        {String.format(state.qrCodeLoginUrlTemplate, state.qrCodeId)}
                                    </FormField>
                                </Grid>
                            </GridRow>
                        }
                        {state.showChangePasswordDialog &&
                            <UserChangePasswordDialog
                                minPasswordLength={state.minPasswordLength}
                                rowId={state.rowId}
                                onOk={this.handleUserChangePasswordDialogOk}
                                onCancel={this.handleUserChangePasswordDialogCancel}
                            />
                        }
                    </>
                }
                actions={
                    <Box display="flex" justifyContent="space-between" width="100%">
                        <DialogItemsNav
                            hasPrevious={false}
                            hasNext={false}
                            onPrevious={null}
                            onNext={null}
                            createUser={state.userProfile.createUser}
                            modifyUser={state.userProfile.modifyUser}
                        />
                        <DialogButtons
                            onCancel={this.handleCancel}
                            onSave={this.handleSave}
                        />
                    </Box>
                }
                onClose={this.handleCancel}/>
        );
    }
}